import { ArrowLeft, Pencil } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import { mensagemDoErro, useDetalhePorSlug } from './cadastros.dados';
import { formatarValor, type DefinicaoDoCadastro } from './cadastros.definicoes';

/**
 * Detalhe de um registro de cadastro de referência (escola, seguradora), lido da
 * API pelo `slug` do endereço.
 *
 * Slug inexistente NÃO é tela em branco nem erro genérico: é
 * `resource-not-found`, com o caminho de volta — o endereço pode ter sido colado
 * errado, ou o registro removido depois que alguém guardou o link.
 *
 * `Editar` só aparece para o cadastro que se edita pela tela. Escola vem do EOL e
 * não tem o botão (regra 7 das regras de interface do CLAUDE.md: o que não se
 * pode fazer não é renderizado). A leitura do detalhe de escola mostra nome de
 * diretor e contato, e a API registra o acesso (regra 7 de API).
 */
export function TelaDeCadastro({ definicao }: { definicao: DefinicaoDoCadastro }) {
  const { slug = '' } = useParams<{ slug: string }>();
  const consulta = useDetalhePorSlug(definicao.grupo, definicao.chave, slug);
  const detalhe = definicao.detalhe;

  if (consulta.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-16">
          <Spinner />
          <p className="text-sm text-muted-foreground">Carregando o registro…</p>
        </CardContent>
      </Card>
    );
  }

  if (consulta.isError) {
    const naoExiste = consulta.error instanceof ErroApi && consulta.error.situacao === 404;
    return naoExiste ? (
      <ErrorState
        variant="resource-not-found"
        title={`${definicao.nomeDaEntidade} não encontrada`}
        description="O endereço pode estar errado, ou o registro pode ter saído do cadastro."
        primaryAction={{ label: definicao.titulo, to: definicao.caminho }}
      />
    ) : (
      <ErrorState
        variant="load-failed"
        title="Não foi possível carregar"
        description={mensagemDoErro(consulta.error, 'Tente de novo em instantes.')}
        primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        secondaryAction={{ label: definicao.titulo, to: definicao.caminho, variant: 'outline' }}
      />
    );
  }

  const registro = consulta.data;
  if (!registro || !detalhe) return null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={definicao.caminho}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {definicao.titulo}
        </Link>
      </div>

      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight">{String(registro.nome)}</h1>
        <Badge variant={registro.situacao === 'inativo' ? 'secondary' : 'default'}>
          {registro.situacao === 'inativo'
            ? definicao.situacaoFeminina
              ? 'Inativa'
              : 'Inativo'
            : definicao.situacaoFeminina
              ? 'Ativa'
              : 'Ativo'}
        </Badge>
        {!definicao.somenteConsulta && (
          <Button asChild variant="outline" size="sm" className="ml-auto">
            <Link to={`${definicao.caminho}/${registro.slug}/editar`}>
              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
              Editar
            </Link>
          </Button>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{detalhe.titulo}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {detalhe.campos.map((campo) => (
              <div key={campo.rotulo} className="space-y-0.5">
                <dt className="text-xs text-muted-foreground">{campo.rotulo}</dt>
                <dd className="break-words text-sm">
                  {typeof campo.campo === 'function'
                    ? campo.campo(registro)
                    : formatarValor(registro[campo.campo])}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

export default TelaDeCadastro;
